import { Header } from '../components/Header.js';
import { QuirkIonContent } from '../components/QuirkIonContent.js';
import { QuirkIonPage } from '../components/QuirkIonPage.js';
import { IconArrowBack, IconDownload, IconPaperAirplane } from '../components/QuirkIcons/index.js';
import { IconButton } from '../components/IconButton.js';
import { useHistory, useParams } from 'react-router';
import { MessageItem } from '../components/MessageItem.js';
import { useGetProfileQuery } from '../api/UserApi.js';
import { ConversationMenu } from '../components/ConversationMenu.js';
import { useState } from 'react';
import { DIRECT_MESSAGE_MAX_LENGTH, EmptyDirectMessage, FullDirectMessage, getNow } from 'model';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { BottomToolbar } from '../components/BottomToolbar.js';
import { FlexibleTextarea } from '../components/Forms/FlexibleTextarea.js';
import styles from './ConversationPage.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { setUser, sendMessage, MessagesState, fetchPrevious } from '../state/messagesSlice.js';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { QuirkVirtualList } from '../components/QuirkVirtualList.js';
import { IonSpinner, IonText, useIonViewDidLeave, useIonViewWillEnter } from '@ionic/react';
import { ContentPreloader } from '../components/ContentPreloader.js';

const ListFooter = () => <div style={{ height: 9 }}></div>;
const listComponents = { Footer: ListFooter } as const;

export function ConversationPage() {
	const self = useCurrentUser();
	const {
		messages,
		user: currentUserId = '',
		loadingPrevious,
		loadingInitial
	} = useSelector((state: { messages: MessagesState }) => state.messages.conversation);
	const { userId } = useParams<{ userId: string; messageId?: string }>();
	const history = useHistory();
	const { data: user } = useGetProfileQuery(currentUserId, { skip: !currentUserId });
	const [currentMessage, setCurrentMessage] = useState<{ message: string; image: any }>({
		message: '',
		image: undefined
	});
	const onMessageChange = (message: string) => {
		setCurrentMessage(current => ({
			...current,
			message
		}));
	};
	const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
	const handleSendMessage = async () => {
		if (currentMessage.message.trim()) {
			dispatch(
				sendMessage({
					...EmptyDirectMessage,
					created: getNow(),
					to: userId,
					from: self._id,
					message: currentMessage.message
				})
			);
			setCurrentMessage({ message: '', image: undefined });
		}
	};
	useIonViewWillEnter(() => {
		if (userId !== currentUserId) {
			dispatch(setUser(userId));
		}
	});
	useIonViewDidLeave(() => {
		dispatch(setUser(undefined));
	});
	const atTopStateChange = (atTop: boolean) => {
		if (atTop && loadingPrevious === false) {
			dispatch(fetchPrevious());
		}
	};
	const finalMessages = [
		<div className={styles.loadPreviousContainer} onClick={() => !loadingPrevious && dispatch(fetchPrevious())}>
			{loadingPrevious ? <IonSpinner /> : <IconDownload />}
			<IonText>{loadingPrevious ? 'Loading...' : 'Load Previous'}</IonText>
		</div>,
		...messages
	];

	if (messages.length === 0 && loadingInitial) {
		finalMessages.push(
			<div className={styles.loadingContainer}>
				<ContentPreloader message={'Loading...'} />
			</div>
		);
	}

	return (
		<QuirkIonPage name="Messages">
			<Header
				title={user?.username}
				left={
					<IconButton
						icon={<IconArrowBack />}
						onClick={() => {
							history.goBack();
						}}
					/>
				}
				right={<ConversationMenu user={user} />}
				testId="MessagesHeader"
			/>
			<QuirkIonContent flush>
				<div className={styles.conversationContainer}>
					<QuirkVirtualList
						items={finalMessages}
						ItemComponent={MessageItem}
						followOutput={atBottom => (atBottom ? 'auto' : false)}
						atTopStateChange={atTopStateChange}
						components={listComponents}
						initialTopMostItemIndex={messages?.length}
						itemKey={(item: FullDirectMessage) => item._id}
					/>
					<BottomToolbar>
						<div className={styles.controls}>
							<div className={styles.inputWrapper}>
								<FlexibleTextarea
									value={currentMessage.message}
									onChange={onMessageChange}
									testId="NewMessageTextarea"
									onEnter={handleSendMessage}
									maxLength={DIRECT_MESSAGE_MAX_LENGTH}
								/>
							</div>
							<IconButton
								icon={<IconPaperAirplane />}
								disabled={!currentMessage.message || currentMessage.message.trim().length === 0}
								onClick={handleSendMessage}
								testId="SendNewMessageButton"
							/>
						</div>
					</BottomToolbar>
				</div>
			</QuirkIonContent>
		</QuirkIonPage>
	);
}
